import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import ScrollToTop from "./ScrollToTop";
import SEO from "../components/Layout/SEO";

import RoadmapSection from "../components/Roadmap/RoadmapSection";
import {
  Router,
  Security,
  Business,
  ArrowForward,
  Message,
  PhoneInTalk,
  Storage,
  CallMerge,
  Shield,
  SmartToy,
} from "@mui/icons-material";

const RoadmapPage = () => {
  const currentSolutions = [
    {
      id: 1,
      icon: <Message fontSize="large" color="primary" />,
      title: "Bulk Messaging Gateway (BMG)",
      description:
        "Telecom-grade SMS platform engineered for 1M+ messages per minute with SMPP and HTTP APIs, multi-operator routing, and full DLT compliance.",
    },
    {
      id: 2,
      icon: <PhoneInTalk fontSize="large" color="primary" />,
      title: "Voice Communications Platform",
      description:
        "Cloud-hosted voice platform with dynamic calling APIs, IVR builder, call bridging, TTS, ASR, and multi-lingual support.",
    },
    {
      id: 3,
      icon: <Storage fontSize="large" color="primary" />,
      title: "App-Layer SMSC",
      description:
        "Custom-built SMSC supporting 100K+ TPS throughput with direct operator connectivity, high availability, and secure message handling.",
    },
    {
      id: 4,
      icon: <CallMerge fontSize="large" color="primary" />,
      title: "Call Completion Suite",
      description:
        "Maximizes outreach by detecting unreachable users and delivering automated voice alerts or missed call SMS for revenue recovery.",
    },
    {
      id: 5,
      icon: <Shield fontSize="large" color="primary" />,
      title: "SMS Firewall & SPAM Guard",
      description:
        "Advanced protection against grey routes and SIM box activity with AI-driven UCC detection and regulatory compliance.",
    },
    {
      id: 6,
      icon: <SmartToy fontSize="large" color="primary" />,
      title: "Telecom API Gateway",
      description:
        "Unified API platform for SMS, voice, email, and RCS with custom adapters for operator backends and enterprise CRMs.",
    },
  ];

  return (
    <>
      <SEO
        title="Innovation Roadmap"
        description="Explore ProtoDiag Solutions' innovation roadmap and vision for the future of telecom communication. Discover our strategic plans and upcoming solutions."
        keywords="ProtoDiag roadmap, telecom innovation roadmap, communication infrastructure roadmap, future telecom solutions"
        url="https://www.protodiag.com/roadmap"
      />
      <ScrollToTop />

      {/* Hero Section */}
      <Box
        sx={{
          py: 10,
          backgroundColor: "primary.main",
          color: "white",
        }}
      >
        <Container>
          <Typography
            variant="h2"
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            Innovation Roadmap
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 800, mb: 4 }}>
            Building the future of telecom communication—enabling intelligent,
            secure, and real-time connectivity across networks, platforms, and
            people
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            endIcon={<ArrowForward />}
            sx={{ fontWeight: "bold" }}
            onClick={() => {
              const teamSection = document.getElementById("vision-section");
              if (teamSection) {
                teamSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            Explore Our Vision
          </Button>
        </Container>
      </Box>

      {/* Strategic Roadmap Section */}
      <RoadmapSection />

      {/* Current Solutions */}
      <Box sx={{ py: 8 }}>
        <Container>
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            gutterBottom
          >
            Our Current Communication Solutions
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 5 }}>
            At ProtoDiag Solutions, we specialize in high-TPS messaging and
            voice delivery platforms, application-layer SMSC solutions,
            AI-powered spam detection, and real-time DLR/CDR generation—all
            designed to support massive volumes and integrate seamlessly with
            operator backbones.
          </Typography>

          <Grid container spacing={4}>
            {currentSolutions.map((solution) => (
              <Grid item xs={12} md={6} lg={4} key={solution.id}>
                <Card elevation={2} sx={{ height: "100%" }}>
                  <CardContent>
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                    >
                      {solution.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      align="center"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {solution.title}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {solution.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Vision Statement */}
      <Box
        id="vision-section"
        sx={{ py: 8, backgroundColor: "background.default" }}
      >
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                component="h2"
                fontWeight="bold"
                gutterBottom
                color="primary.main"
              >
                Our Vision
              </Typography>
              <Typography
                variant="h6"
                paragraph
                sx={{ mb: 3, fontStyle: "italic" }}
              >
                "To be the global catalyst for next-generation
                communication—enabling intelligent, secure, and real-time
                connectivity across networks, platforms, and people."
              </Typography>
              <Typography variant="body1" paragraph>
                We envision a world where digital communication is
                limitless—transcending barriers of scale, complexity, and
                geography. ProtoDiag is committed to building intelligent,
                interconnected ecosystems where businesses, telecom operators,
                and users experience seamless and secure communication.
              </Typography>
              <Typography variant="body1">
                Our future-ready platforms are designed to redefine how the
                world connects, engages, and transacts.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Card
                  elevation={3}
                  sx={{ p: 3, borderLeft: 6, borderColor: "primary.main" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Router color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Innovation
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    Building future-ready telecom platforms
                  </Typography>
                </Card>

                <Card
                  elevation={3}
                  sx={{ p: 3, borderLeft: 6, borderColor: "primary.main" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Security color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Integrity
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    Operating with trust and compliance
                  </Typography>
                </Card>

                <Card
                  elevation={3}
                  sx={{ p: 3, borderLeft: 6, borderColor: "primary.main" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Business color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Customer Success
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    Enabling excellence through reliable services
                  </Typography>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default RoadmapPage;
